import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, MessageSquare, FileText, Brain, FolderKanban, Users, UserPlus, Briefcase, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller } from "@/pages/_shared";

export default function Search() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const { activeCompany, isGroupView } = useOrg();

  const run = async (val) => {
    setQ(val);
    if (!val.trim()) { setRes(null); return; }
    const { data } = await api.get("/search", { params: { q: val } });
    setRes(data);
  };

  const groups = res ? [
    { key: "conversations", label: "Chats", icon: MessageSquare, items: res.conversations || [], to: (i) => `/app/chat/${i.id}`, text: (i) => i.title },
    { key: "customers", label: "Customers", icon: Users, items: res.customers || [], to: () => `/app/crm/customers`, text: (i) => i.data?.name || "Customer" },
    { key: "leads", label: "Leads", icon: UserPlus, items: res.leads || [], to: () => `/app/crm/leads`, text: (i) => i.data?.name || "Lead" },
    { key: "jobs", label: "Jobs", icon: Briefcase, items: res.jobs || [], to: () => `/app/jobs`, text: (i) => i.title || i.customer_name },
    { key: "invoices", label: "Invoices", icon: Receipt, items: res.invoices || [], to: () => `/app/invoices`, text: (i) => `${i.number || ""} ${i.customer_name || ""}` },
    { key: "quotes", label: "Quotes", icon: FileText, items: res.quotes || [], to: () => `/app/quotes`, text: (i) => `${i.number || ""} ${i.title || i.customer_name || ""}` },
    { key: "files", label: "Documents", icon: FileText, items: res.files || [], to: () => `/app/files`, text: (i) => i.filename },
    { key: "memories", label: "Memory", icon: Brain, items: res.memories || [], to: () => `/app/memory`, text: (i) => i.content },
    { key: "projects", label: "Projects", icon: FolderKanban, items: res.projects || [], to: (i) => `/app/projects/${i.id}`, text: (i) => i.name },
  ] : [];
  const total = res ? groups.reduce((n, g) => n + g.items.length, 0) : 0;

  return (
    <Scroller>
      <PageHeader title="Search" subtitle={isGroupView ? "Find anything across the whole group" : `Searching within ${activeCompany?.name || "this business"}`} icon={SearchIcon} />
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-gold/25 bg-graphite px-4 py-3">
        <SearchIcon className="h-4 w-4 text-gold/70" />
        <input autoFocus value={q} onChange={(e) => run(e.target.value)} placeholder="Search chats, files, memory, projects…" data-testid="global-search-input"
          className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none" />
      </div>

      {res && total === 0 && <p className="text-center text-sm text-white/35">No results for "{q}"</p>}

      <div className="space-y-6">
        {groups.filter((g) => g.items.length > 0).map((g) => (
          <div key={g.key}>
            <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-white/40"><g.icon className="h-3.5 w-3.5" /> {g.label}</p>
            <div className="space-y-1.5">
              {g.items.map((i, idx) => (
                <button key={idx} onClick={() => nav(g.to(i))} className="block w-full truncate rounded-lg border border-white/8 bg-charcoal px-4 py-2.5 text-left text-sm text-white/75 hover:border-gold/25 hover:text-white">
                  {g.text(i)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Scroller>
  );
}
